import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { 
  Clock, 
  Users, 
  TrendingUp, 
  Gift, 
  Star,
  Share2,
  Trophy,
  Target,
  ArrowUp,
  ExternalLink,
  Copy,
  Facebook,
  Twitter,
  Mail,
  MessageCircle,
  Crown,
  Zap
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const HoldingTankDashboard = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  
  const [userInfo, setUserInfo] = useState(null);
  const [countdown, setCountdown] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [tree, setTree] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    if (userId) {
      loadUserInfo();
      loadCountdown();
      loadLeaderboard();
      loadReferralTree();
      
      // Update countdown every second
      const interval = setInterval(loadCountdown, 1000);
      return () => clearInterval(interval);
    }
  }, [userId]);

  const loadUserInfo = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/holding/me/${userId}`);
      if (response.data.ok) {
        setUserInfo(response.data.queue_info);
      }
    } catch (error) {
      console.error('Error loading user info:', error);
      toast.error('Failed to load your information');
    } finally {
      setLoading(false);
    }
  };

  const loadCountdown = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/holding/countdown`);
      if (response.data.ok) {
        setCountdown(response.data.countdown);
      }
    } catch (error) {
      console.error('Error loading countdown:', error);
    }
  };

  const loadLeaderboard = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/holding/leaderboard?limit=10`);
      if (response.data.ok) {
        setLeaderboard(response.data.leaderboard);
      }
    } catch (error) {
      console.error('Error loading leaderboard:', error);
    }
  };

  const loadReferralTree = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/holding/tree/${userId}?depth=2`);
      if (response.data.ok) {
        setTree(response.data.tree);
      }
    } catch (error) {
      console.error('Error loading referral tree:', error);
    }
  };

  const formatTime = (seconds) => {
    const days = Math.floor(seconds / (24 * 60 * 60));
    const hours = Math.floor((seconds % (24 * 60 * 60)) / (60 * 60));
    const minutes = Math.floor((seconds % (60 * 60)) / 60);
    const secs = seconds % 60;

    return { days, hours, minutes, seconds: secs };
  };

  const copyReferralLink = () => {
    if (userInfo?.referral_link) {
      navigator.clipboard.writeText(userInfo.referral_link);
      toast.success('Referral link copied to clipboard!');
    }
  };

  const shareOnSocial = (platform) => {
    const referralLink = userInfo?.referral_link;
    const message = `Join me in the BBC Quick List pre-launch queue! Early access to the most powerful MLM platform. ${referralLink}`;
    
    let shareUrl = '';
    
    switch (platform) {
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(referralLink)}`;
        break;
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(message)}`;
        break;
      case 'email':
        shareUrl = `mailto:?subject=Join BBC Quick List Early Access&body=${encodeURIComponent(message)}`;
        break;
      default:
        return;
    }
    
    window.open(shareUrl, '_blank', 'width=600,height=400');
  };

  const getRankIcon = (rank) => {
    if (rank <= 3) return <Crown className="w-4 h-4 text-yellow-500" />;
    if (rank <= 10) return <Trophy className="w-4 h-4 text-orange-500" />;
    return <Star className="w-4 h-4 text-blue-500" />;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'launched': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const renderReferralTree = (node, level = 0) => {
    if (!node) return null;
    
    const indent = level * 20;
    
    return (
      <div key={node.user_id} className="space-y-2">
        <div 
          className={`flex items-center space-x-3 p-3 rounded-lg border-l-4 ${
            level === 0 ? 'border-yellow-400 bg-yellow-50' : 
            level === 1 ? 'border-blue-400 bg-blue-50' : 
            'border-green-400 bg-green-50'
          }`}
          style={{ marginLeft: `${indent}px` }}
        >
          <div className={`w-2 h-2 rounded-full ${
            node.status === 'active' ? 'bg-green-500' : 'bg-yellow-500'
          }`} />
          <div className="flex-1">
            <div className="font-medium text-sm">{node.full_name}</div>
            <div className="text-xs text-gray-500">
              #{node.queue_position} • {node.referral_count} referrals
            </div>
          </div>
          <Badge className={getStatusColor(node.status)} variant="outline">
            {node.status}
          </Badge>
        </div>
        
        {node.children && node.children.map(child => renderReferralTree(child, level + 1))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-yellow-50 via-orange-50 to-green-50">
        <div className="container mx-auto px-4 py-8">
          <div className="animate-pulse space-y-6">
            <div className="h-8 bg-gray-200 rounded w-1/3"></div>
            <div className="grid gap-6 md:grid-cols-3">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="h-48 bg-gray-200 rounded-lg"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!userInfo) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-yellow-50 via-orange-50 to-green-50">
        <div className="container mx-auto px-4 py-8">
          <div className="text-center py-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">User Not Found</h2>
            <p className="text-gray-600 mb-6">The user information could not be loaded.</p>
            <Button onClick={() => navigate('/join')}>
              Join the Queue
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const user = userInfo.user;
  const stats = userInfo.stats;
  const userRank = leaderboard.findIndex(u => u.user_id === userId) + 1;

  return (
    <div className="min-h-screen bg-gradient-to-br from-yellow-50 via-orange-50 to-green-50">
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-7xl mx-auto">
          
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold text-gray-900 mb-2">
              Welcome, {user.full_name}!
            </h1>
            <p className="text-xl text-gray-600">Your BBC Quick List Dashboard</p>
          </div>

          {/* Top Stats Row */}
          <div className="grid gap-6 md:grid-cols-4 mb-8">
            
            {/* Queue Position */}
            <Card className="border-2 border-yellow-400 bg-yellow-50">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg text-yellow-800">Queue Position</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-yellow-600 mb-1">
                  #{user.queue_position}
                </div>
                <p className="text-sm text-yellow-700">
                  {user.queue_position <= 10 ? 'Top 10!' : user.queue_position <= 50 ? 'Top 50!' : 'In Queue'}
                </p>
              </CardContent>
            </Card>

            {/* Referrals */}
            <Card className="border-2 border-blue-400 bg-blue-50">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg text-blue-800">Your Referrals</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-blue-600 mb-1">
                  {stats?.verified_referrals || 0}
                </div>
                <p className="text-sm text-blue-700">
                  {stats?.direct_referrals > stats?.verified_referrals ? 
                    `${stats.direct_referrals - stats.verified_referrals} pending` : 
                    'All verified'}
                </p>
              </CardContent>
            </Card>

            {/* Leaderboard Rank */}
            <Card className="border-2 border-purple-400 bg-purple-50">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg text-purple-800 flex items-center space-x-2">
                  {getRankIcon(userRank)}
                  <span>Leaderboard</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-purple-600 mb-1">
                  {userRank > 0 ? `#${userRank}` : '--'}
                </div>
                <p className="text-sm text-purple-700">
                  {userRank <= 10 ? 'Top Performer!' : 'Keep referring!'}
                </p>
              </CardContent>
            </Card>

            {/* Status */}
            <Card className="border-2 border-green-400 bg-green-50">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg text-green-800">Status</CardTitle>
              </CardHeader>
              <CardContent>
                <Badge className={`${getStatusColor(user.status)} text-lg px-3 py-1`}>
                  {user.status.toUpperCase()}
                </Badge>
                <p className="text-sm text-green-700 mt-2">
                  {user.email_verified ? '✓ Email verified' : '⚠ Verify email'}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Countdown Timer */}
          {countdown && !countdown.is_launched && (
            <Card className="mb-8 border-2 border-orange-400 bg-gradient-to-r from-orange-100 to-yellow-100">
              <CardHeader>
                <CardTitle className="text-2xl text-center text-orange-800 flex items-center justify-center space-x-2">
                  <Clock className="w-6 h-6" />
                  <span>Time Until Launch</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-4 gap-4 max-w-md mx-auto">
                  {Object.entries(formatTime(countdown.seconds_remaining)).map(([unit, value]) => (
                    <div key={unit} className="text-center">
                      <div className="text-3xl font-bold text-orange-600">{value}</div>
                      <div className="text-sm uppercase text-orange-700">{unit}</div>
                    </div>
                  ))}
                </div>
                <div className="mt-4">
                  <Progress 
                    value={Math.max(0, 100 - (countdown.seconds_remaining / (45 * 24 * 60 * 60) * 100))} 
                    className="h-3"
                  />
                </div>
              </CardContent>
            </Card>
          )}

          {/* Tab Navigation */}
          <div className="mb-6">
            <div className="border-b border-gray-200">
              <nav className="-mb-px flex space-x-8">
                {[
                  { id: 'overview', label: 'Overview', icon: Target },
                  { id: 'referrals', label: 'My Referrals', icon: Users },
                  { id: 'leaderboard', label: 'Leaderboard', icon: Trophy },
                  { id: 'share', label: 'Share & Earn', icon: Share2 }
                ].map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center space-x-2 py-2 px-1 border-b-2 font-medium text-sm ${
                      activeTab === tab.id
                        ? 'border-yellow-500 text-yellow-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <tab.icon className="w-4 h-4" />
                    <span>{tab.label}</span>
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* Tab Content */}
          <div className="grid gap-6">
            
            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <div className="grid gap-6 lg:grid-cols-2">
                <Card>
                  <CardHeader>
                    <CardTitle>Quick Stats</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">Email Verified</span>
                      <Badge className={user.email_verified ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}>
                        {user.email_verified ? 'Yes' : 'Pending'}
                      </Badge>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">Joined</span>
                      <span className="font-medium">
                        {new Date(user.join_timestamp).toLocaleDateString()}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">Country</span>
                      <span className="font-medium">{user.country}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">Referral Code</span>
                      <code className="bg-gray-100 px-2 py-1 rounded text-sm">
                        {user.referral_code}
                      </code>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Engagement Score</CardTitle>
                    <CardDescription>Based on your activity and referrals</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="text-center">
                      <div className="text-4xl font-bold text-blue-600 mb-2">
                        {stats?.engagement_score?.toFixed(1) || '0.0'}
                      </div>
                      <Progress value={Math.min(stats?.engagement_score || 0, 100)} className="mb-4" />
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <div className="font-medium">Login Count</div>
                          <div className="text-gray-600">{user.login_count || 0}</div>
                        </div>
                        <div>
                          <div className="font-medium">Shares</div>
                          <div className="text-gray-600">{user.shares_count || 0}</div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Referrals Tab */}
            {activeTab === 'referrals' && (
              <Card>
                <CardHeader>
                  <CardTitle>Your Referral Network</CardTitle>
                  <CardDescription>People you've invited to join</CardDescription>
                </CardHeader>
                <CardContent>
                  {tree ? (
                    <div className="space-y-4">
                      {renderReferralTree(tree)}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-gray-900 mb-2">No Referrals Yet</h3>
                      <p className="text-gray-600 mb-4">Start sharing your referral link to build your network!</p>
                      <Button onClick={() => setActiveTab('share')}>
                        Start Sharing
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Leaderboard Tab */}
            {activeTab === 'leaderboard' && (
              <Card>
                <CardHeader>
                  <CardTitle>Top Referrers</CardTitle>
                  <CardDescription>See who's leading the pack</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {leaderboard.slice(0, 10).map((leader, index) => (
                      <div 
                        key={leader.user_id} 
                        className={`flex items-center space-x-4 p-3 rounded-lg ${
                          leader.user_id === userId ? 'bg-yellow-50 border-2 border-yellow-200' : 'bg-gray-50'
                        }`}
                      >
                        <div className="flex-shrink-0">
                          {index < 3 ? (
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                              index === 0 ? 'bg-yellow-100 text-yellow-800' :
                              index === 1 ? 'bg-gray-100 text-gray-800' :
                              'bg-orange-100 text-orange-800'
                            }`}>
                              {index === 0 ? '🥇' : index === 1 ? '🥈' : '🥉'}
                            </div>
                          ) : (
                            <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-800 flex items-center justify-center text-sm font-bold">
                              {index + 1}
                            </div>
                          )}
                        </div>
                        
                        <div className="flex-1">
                          <div className="font-medium">
                            {leader.full_name}
                            {leader.user_id === userId && <span className="text-yellow-600 ml-2">(You)</span>}
                          </div>
                          <div className="text-sm text-gray-600">
                            {leader.referral_count} referrals • #{leader.queue_position} in queue
                          </div>
                        </div>
                        
                        <div className="flex items-center space-x-2">
                          {leader.badges?.map((badge, i) => (
                            <Badge key={i} variant="outline" className="text-xs">
                              {badge}
                            </Badge>
                          ))}
                          <div className="text-right">
                            <div className="text-sm font-medium text-green-600">
                              {leader.engagement_score?.toFixed(1) || '0.0'}
                            </div>
                            <div className="text-xs text-gray-500">score</div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  <div className="text-center mt-6">
                    <Button variant="outline" onClick={() => navigate('/leaderboard')}>
                      View Full Leaderboard
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Share Tab */}
            {activeTab === 'share' && (
              <div className="grid gap-6 lg:grid-cols-2">
                <Card>
                  <CardHeader>
                    <CardTitle>Your Referral Link</CardTitle>
                    <CardDescription>Share this link to invite friends</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="bg-gray-50 p-4 rounded-lg border-2 border-dashed border-gray-300">
                      <div className="text-center mb-4">
                        <div className="text-lg font-bold text-blue-600 mb-2">
                          {user.referral_code}
                        </div>
                        <div className="text-sm text-gray-600 break-all">
                          {userInfo.referral_link}
                        </div>
                      </div>
                      <Button onClick={copyReferralLink} className="w-full">
                        <Copy className="w-4 h-4 mr-2" />
                        Copy Link
                      </Button>
                    </div>
                    
                    <div className="text-center">
                      <p className="text-sm text-gray-600 mb-4">
                        For every 3 verified referrals, you move up 5 positions in the queue!
                      </p>
                      <div className="flex items-center justify-center space-x-1 text-yellow-600">
                        <Zap className="w-4 h-4" />
                        <span className="text-sm font-medium">
                          {stats?.verified_referrals || 0} / 3 referrals for bonus
                        </span>
                      </div>
                      <Progress 
                        value={((stats?.verified_referrals || 0) / 3) * 100} 
                        className="mt-2 h-2"
                      />
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Share on Social Media</CardTitle>
                    <CardDescription>Spread the word and climb the leaderboard</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <Button 
                      onClick={() => shareOnSocial('facebook')}
                      className="w-full bg-blue-600 hover:bg-blue-700"
                    >
                      <Facebook className="w-4 h-4 mr-2" />
                      Share on Facebook
                    </Button>
                    
                    <Button 
                      onClick={() => shareOnSocial('twitter')}
                      className="w-full bg-sky-500 hover:bg-sky-600"
                    >
                      <Twitter className="w-4 h-4 mr-2" />
                      Share on Twitter
                    </Button>
                    
                    <Button 
                      onClick={() => shareOnSocial('email')}
                      variant="outline"
                      className="w-full"
                    >
                      <Mail className="w-4 h-4 mr-2" />
                      Share via Email
                    </Button>
                    
                    <div className="pt-4 border-t">
                      <div className="text-sm text-gray-600 space-y-2">
                        <div className="flex items-center space-x-2">
                          <ArrowUp className="w-4 h-4 text-green-500" />
                          <span>Move up in queue faster</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Gift className="w-4 h-4 text-blue-500" />
                          <span>Earn exclusive bonuses</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Trophy className="w-4 h-4 text-yellow-500" />
                          <span>Compete for top spots</span>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default HoldingTankDashboard;